//React
import * as React from 'react';
//Material UI
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  InputAdornment
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';

import { useGetResidentsAnalyticsQuery } from '../../api/apiSlice';
export default function ResidentsAnalytics() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [searchTerm, setSearchTerm] = React.useState('');

  const {
    data: residentsData,
    isLoading: residentsLoading,
    isError: residentsError,
    error: residentsMessage,
  } = useGetResidentsAnalyticsQuery();

  const filteredData = React.useMemo(() => {
    if (!residentsData?.serial_access_counts) return [];

    return residentsData.serial_access_counts.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      const serialMatch = item.serial_number?.toLowerCase().includes(searchLower);
      const dateMatch = item.access_timestamps[0] ?
        new Date(item.access_timestamps[0]).toLocaleDateString().toLowerCase().includes(searchLower) : false;

      return serialMatch || dateMatch;
    });
  }, [residentsData, searchTerm]);

  const paginatedData = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };



  if (residentsLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

 else if (residentsError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Error loading residents analytics: {residentsMessage?.data?.message || 'Unknown error'}
        </Alert>
      </Container>
    );
  }
else{

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Residents Analytics Dashboard
      </Typography>

      <Grid container spacing={3}>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Users Accessed Today
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {residentsData?.unique_users_accessed_today || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="secondary">
                Total Users Accessed to Date
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                {residentsData?.accumalative_users_accessed_to_date || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Serial Access Details
              </Typography>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by serial number or date..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {filteredData.length > 0 ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Serial Number</strong></TableCell>
                        <TableCell align="right"><strong>Access Count</strong></TableCell>
                        <TableCell align="right"><strong>Last Access</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{item.serial_number || 'N/A'}</TableCell>
                          <TableCell align="right">{item.total_access_count || 0}</TableCell>
                          <TableCell align="right">
                            {item.access_timestamps[0] ? new Date(item.access_timestamps[0]).toLocaleDateString() : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={filteredData.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                  />
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No serial access data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}}
